import React from "react";

/* Reusable admin UI primitives. Keep them small and unopinionated. */

export function StatCard({ label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-3"
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className="text-2xl text-zinc-100 font-medium tabular-nums mt-1">
        {value ?? "—"}
      </div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    running: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    partial: "bg-amber-500/20 text-amber-200 border-amber-500/40",
    failed: "bg-rose-500/20 text-rose-300 border-rose-500/40",
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    inactive: "bg-zinc-700/30 text-zinc-400 border-zinc-700",
    archived: "bg-zinc-800/40 text-zinc-500 border-zinc-800",
    duplicate: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  };
  const cls = map[status] || "bg-zinc-700/30 text-zinc-400 border-zinc-700";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${cls}`}>
      {status}
    </span>
  );
}

export function ImageHealthBadge({ value }) {
  const map = {
    alive: "bg-emerald-500/20 text-emerald-300",
    dead_on_check: "bg-rose-500/20 text-rose-300",
    unchecked: "bg-zinc-700/30 text-zinc-400",
  };
  const cls = map[value] || "bg-zinc-700/30 text-zinc-400";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{value}</span>
  );
}

/** A horizontal bar list — label + count + bar proportional to max. */
export function BarList({ items, total, testid }) {
  const max = Math.max(1, ...items.map((it) => it.value));
  return (
    <div data-testid={testid} className="space-y-1.5">
      {items.length === 0 && (
        <div className="text-xs text-zinc-500">(no data)</div>
      )}
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-3 text-xs">
          <div className="w-24 truncate text-zinc-400">{it.label}</div>
          <div className="flex-1 h-2 rounded bg-zinc-900 overflow-hidden">
            <div
              className="h-full bg-emerald-500/60"
              style={{ width: `${(it.value / max) * 100}%` }}
            />
          </div>
          <div className="w-14 text-right text-zinc-300 tabular-nums">
            {it.value.toLocaleString("de-AT")}
            {total ? (
              <span className="text-zinc-500"> ({Math.round((it.value / total) * 100)}%)</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Section({ title, action, children, testid }) {
  return (
    <section data-testid={testid} className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-xs uppercase tracking-widest text-zinc-400 font-mono">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function ShortId({ value, length = 8 }) {
  if (!value) return <span className="text-zinc-600">—</span>;
  return (
    <span title={value} className="font-mono text-[11px]">
      {value.slice(0, length)}…
    </span>
  );
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
